import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useAuth,
} from '@aaas/shared';
import {
  useCreateEmailSignatureMutation,
  useUpdateEmailSignatureMutation,
} from '../hooks/useEmailSignatureQueries';
import { sanitizeHtml } from '../utils/sanitize-html';
import { TiptapEmailEditor } from './TiptapEmailEditor';
import type { EmailSignature } from '../types';

interface EmailSignatureFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  signature?: EmailSignature | null;
  onSuccess: () => void;
}

export function EmailSignatureFormDialog({
  open,
  onClose,
  mode,
  signature,
  onSuccess,
}: EmailSignatureFormDialogProps) {
  const { t } = useTranslation('ats');
  const { user, currentWorkspace } = useAuth();
  const isEdit = mode === 'edit' && !!signature;

  const [name, setName] = useState(isEdit ? signature.name : '');
  const [rawHtml, setRawHtml] = useState(isEdit ? signature.raw_html : '');
  const [isDefault, setIsDefault] = useState(isEdit ? signature.is_default : false);
  const [bodyTab, setBodyTab] = useState<'edit' | 'preview'>('edit');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateEmailSignatureMutation();
  const updateMutation = useUpdateEmailSignatureMutation();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !currentWorkspace) return;

    if (!name.trim()) {
      setError(t('common.validation.nameRequired'));
      return;
    }
    if (!rawHtml.trim()) {
      setError(t('emailSignature.form.errors.bodyRequired'));
      return;
    }

    setError(null);
    try {
      if (mode === 'create') {
        await createMutation.mutateAsync({
          name: name.trim(),
          raw_html: rawHtml,
          is_default: isDefault,
        });
      } else if (signature) {
        await updateMutation.mutateAsync({
          signatureId: signature.id,
          data: {
            name: name.trim(),
            raw_html: rawHtml,
            is_default: isDefault,
          },
        });
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('config.unknownError'));
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('emailSignature.form.createTitle') : t('emailSignature.form.editTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="signature-name">{t('emailSignature.form.nameLabel')}</Label>
            <Input
              id="signature-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('emailSignature.form.namePlaceholder')}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('emailSignature.form.signatureLabel')}</Label>
              <div className="flex rounded-md border overflow-hidden text-sm">
                <button
                  type="button"
                  onClick={() => setBodyTab('edit')}
                  className={`px-3 py-1 transition-colors ${
                    bodyTab === 'edit'
                      ? 'bg-orange-50 text-orange-700 font-medium'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {t('emailSignature.form.tabEdit')}
                </button>
                <button
                  type="button"
                  onClick={() => setBodyTab('preview')}
                  className={`px-3 py-1 border-l transition-colors ${
                    bodyTab === 'preview'
                      ? 'bg-orange-50 text-orange-700 font-medium'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {t('emailSignature.form.tabPreview')}
                </button>
              </div>
            </div>

            {bodyTab === 'edit' ? (
              <TiptapEmailEditor
                value={rawHtml}
                onChange={setRawHtml}
                disabled={isSubmitting}
                placeholder={t('emailSignature.form.bodyPlaceholder')}
              />
            ) : (
              <div className="border rounded-md p-4 bg-white min-h-[200px] max-h-[400px] overflow-y-auto">
                {rawHtml ? (
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(rawHtml) }}
                  />
                ) : (
                  <p className="text-sm text-gray-400 italic">{t('emailSignature.form.noPreview')}</p>
                )}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <Checkbox
              checked={isDefault}
              onCheckedChange={(checked) => setIsDefault(checked === true)}
              disabled={isSubmitting}
            />
            <span className="text-sm text-gray-700">
              {t('emailSignature.form.setAsDefault')}
            </span>
          </label>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {mode === 'create' ? t('common.creating') : t('common.saving')}
                </>
              ) : mode === 'create' ? (
                t('emailSignature.form.createButton')
              ) : (
                t('common.saveChanges')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
